import React from 'react';
import Button from '~/components/base/Button';
import Modal from '~/components/base/Modal';
import Typography from '~/components/base/Typography';
import OTPInput from '~/components/modules/OTPInput';
import lock from '~/assets/icons/lock.svg';
import './admin-mfa-modal.scss';

type Props = {
    className?: string,
    isShow: boolean,
    code: string,
    codeLength: number,
    maskedEmail?: string,
    devCode?: string,
    errorMessage: string,
    resendMessage: string,
    attemptsRemaining?: number,
    /** Codes still available before the block. */
    resendsRemaining?: number,
    /** Resends exhausted: no new code may be asked for, but a live code still works. */
    isResendBlocked: boolean,
    /** Too many wrong codes: nothing can be verified at all until this lifts. */
    isVerifyBlocked: boolean,
    /** Formatted wait left on the verify lockout, undefined when none. */
    blockedLabel?: string,
    isVerifying: boolean,
    isSending: boolean,
    isResendDisabled: boolean,
    /** A code was issued and has since run out of time. */
    isCodeExpired: boolean,
    /** Formatted wait before another code may be requested, undefined when none. */
    cooldownLabel?: string,
    /** Formatted life left in the current code, undefined once it is spent. */
    codeExpiryLabel?: string,
    onChange: (value: string) => void,
    onVerify: () => void,
    onResend: () => void,
    onClose: () => void
}

export default function AdminMfaModal({
    className = '',
    isShow,
    code,
    codeLength,
    maskedEmail,
    devCode,
    errorMessage,
    resendMessage,
    attemptsRemaining,
    resendsRemaining,
    isResendBlocked,
    isVerifyBlocked,
    blockedLabel,
    isVerifying,
    isSending,
    isResendDisabled,
    isCodeExpired,
    cooldownLabel,
    codeExpiryLabel,
    onChange,
    onVerify,
    onResend,
    onClose
}: Props): JSX.Element {
    const resendLabel = cooldownLabel ? `Resend code in ${cooldownLabel}` : 'Resend code';

    return (
        <Modal
            className={`admin-mfa-modal ${className}`}
            title='Verification required'
            // The house width. modal.scss puts min-width 544px on every dialog,
            // which a max-width can never undercut, so 'lg' only widened this to
            // 800px and left the content adrift in it.
            size='sm'
            isShow={isShow}
            onToggle={onClose}
            // A stray click on the backdrop should not close a step the member
            // has to complete; the header's close button still does.
            disableBackgroundClose={true}
        >
            <div className='admin-mfa-modal__body'>
                {/*
                    Locked out, so there is nothing to type. The inputs, the
                    code's expiry countdown and the resend affordance all come
                    off the screen -- leaving them up invited a member to spend
                    the wait typing codes that could not be checked, and the
                    expiry timer in particular said the opposite of what was
                    true: it counted down a code they were no longer allowed to
                    use.
                */}
                {isVerifyBlocked ? (
                    <div className='admin-mfa-modal__locked'>
                        <img src={lock} alt='' className='admin-mfa-modal__locked-icon' />

                        <Typography size={14} color='dark' className='admin-mfa-modal__locked-message'>
                            Too many incorrect codes. Verification is locked for a short while.
                        </Typography>

                        {!!blockedLabel && (
                            <Typography size={20} color='dark' className='admin-mfa-modal__locked-countdown'>
                                {blockedLabel}
                            </Typography>
                        )}
                    </div>
                ) : (
                    <>
                    <Typography size={14} color='dark' className='admin-mfa-modal__intro'>
                        We sent a 6-digit code to <b>{maskedEmail || 'your email'}</b>. Enter it
                        below to carry on.
                    </Typography>

                    {/*
                        The code's real remaining life, counting down, rather than a
                        fixed "expires in 10 minutes" that is only true in the second
                        the email was sent. Arriving on another admin screen re-reads
                        the outstanding challenge, so this picks up where it actually
                        is instead of restarting.
                    */}
                    {isCodeExpired ? (
                        <Typography size={12} color='error' className='admin-mfa-modal__expiry'>
                            That code has expired. Ask for a new one.
                        </Typography>
                    ) : !!codeExpiryLabel && (
                        <Typography size={12} color='davy-grey-500' className='admin-mfa-modal__expiry'>
                            Expires in {codeExpiryLabel}
                        </Typography>
                    )}

                    {/*
                        Only present when the backend returns the code instead of
                        relying on the email — ADMIN_MFA_DEV_MODE, on local, dev
                        and staging. Production never sends the field, so this
                        cannot render there.

                        Never render a stale one. useAdminMfaChallenge clears it
                        the moment a new code is requested, so this is blank for
                        the round trip rather than showing the code the resend has
                        just invalidated.

                        Nor an expired one. The code outlives the request that
                        fetched it, so once its ten minutes are up this is still
                        holding six digits the server will now refuse -- printed
                        directly above a line saying the code has expired, and
                        the obvious thing to do with a code on screen is type
                        it. That spends an attempt, and there are three.
                    */}
                    {!isCodeExpired && !!devCode && (
                        <Typography size={12} color='error' className='admin-mfa-modal__dev-code'>
                            Dev mode: email not sent. Code: <b>{devCode}</b>
                        </Typography>
                    )}

                    {/*
                        OTPInput keeps the digits in its own state and reports them
                        upward through onChange; `value` is in its Props type but
                        the component never reads it, so this is passed to satisfy
                        the contract and the code above is the source of truth.
                    */}
                    <OTPInput
                        value={code}
                        onChange={onChange}
                        message={errorMessage}
                        numberOfInputs={codeLength}
                        label='Verification code'
                        isInLineClipboard={true} />

                    {/*
                        Only past the halfway mark. Telling someone they have two of
                        three tries left before they have made a mistake is
                        alarming for no reason.
                    */}
                    {attemptsRemaining !== undefined && attemptsRemaining > 0 && attemptsRemaining < 2 && (
                        <Typography size={12} color='error' className='admin-mfa-modal__attempts'>
                            {attemptsRemaining} {attemptsRemaining === 1 ? 'attempt left.' : 'attempts left.'}
                        </Typography>
                    )}

                    {/*
                        Only on the last one. Counting codes down from three would
                        make an ordinary resend feel like a warning, and the wait
                        lengthening already tells them something is up.
                    */}
                    {!isResendBlocked && resendsRemaining === 1 && (
                        <Typography size={12} color='davy-grey-500' className='admin-mfa-modal__resends'>
                            That&apos;s your last code. After it you&apos;ll need to wait an hour.
                        </Typography>
                    )}

                    {isResendBlocked ? (
                        <Typography size={12} color='error' className='admin-mfa-modal__blocked'>
                            You&apos;ve asked for too many codes. Try again in an hour, or contact support if
                            you still can&apos;t get in.
                        </Typography>
                    ) : (
                        <Button
                            label={resendLabel}
                            size='lg'
                            variant='link'
                            className='admin-mfa-modal__resend'
                            onClick={onResend}
                            isProcessing={isSending}
                            disabled={isResendDisabled} />
                    )}

                    {/* Resend failures live here, not in OTPInput's message, which
                        clears on the next keystroke. */}
                    {!isResendBlocked && !!resendMessage && (
                        <Typography size={12} color='error' className='admin-mfa-modal__resend-error'>
                            {resendMessage}
                        </Typography>
                    )}

                    </>
                )}

                {/*
                    One button, two jobs. Locked out there is nothing to send,
                    so Confirm just dismisses the modal -- it must not call
                    verify, which would spend a request on a code that cannot
                    be checked and answer with the same refusal.
                */}
                <Button
                    label={isVerifyBlocked ? 'Confirm' : 'Verify'}
                    size='lg'
                    className='w-100 mt-3'
                    isProcessing={!isVerifyBlocked && isVerifying}
                    disabled={!isVerifyBlocked && (code.length !== codeLength || isVerifying || isCodeExpired)}
                    onClick={isVerifyBlocked ? onClose : onVerify} />

                {/* Nothing but the lock, the wait and Confirm while locked. */}
                {!isVerifyBlocked && (
                    <Typography size={12} color='davy-grey-500' className='admin-mfa-modal__footnote'>
                        Verifying keeps this open for two hours. We ask again after that, or if you
                        sign in somewhere else.
                    </Typography>
                )}
            </div>
        </Modal>
    );
}
